"use client";

import { useState } from "react";
import { ArrowRight, ChevronUp, MapPin, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type Glacier = {
  id: number;
  name: string;
  region: string;
  coords: string;
  country: string;
  summary: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  top: string;
  left: string;
};
type Region = {
  id: string;
  label: string;
};
const regions: Region[] = [{
  id: "calgary",
  label: "Calgary"
}, {
  id: "airdrie",
  label: "Airdrie"
}, {
  id: "okotoks",
  label: "Okotoks"
}, {
  id: "cochrane",
  label: "Cochrane"
}, {
  id: "chestermere",
  label: "Chestermere"
}];
const glaciers: Glacier[] = [];
export default function ServiceMap() {
  const [activeRegions, setActiveRegions] = useState<string[]>([]);
  const [selected, setSelected] = useState<Glacier | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const toggleRegion = (id: string) => {
    setActiveRegions(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };
  const visibleGlaciers = activeRegions.length === 0 ? glaciers : glaciers.filter(g => activeRegions.includes(g.region));
  const handleMarkerClick = (glacier: Glacier) => {
    setSelected(glacier);
  };
  return <section data-section-id="14993" id="our-service-region" className="bg-background text-foreground relative min-h-[70vh] md:min-h-screen w-full overflow-x-hidden">
      {}
      <div className="absolute inset-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/real_calgary_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/real_calgary_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/real_calgary_map.png-wvc-sizes" alt="Real illustration of Calgary service area" className="h-full w-full object-cover opacity-80" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-background/60" />
      </div>

      {}
      <div className="absolute inset-0 hidden md:block">
        {visibleGlaciers.map((glacier, i) => <Button key={glacier.id} data-index={i} onClick={() => handleMarkerClick(glacier)} onMouseEnter={() => setHovered(glacier.id)} onMouseLeave={() => setHovered(null)} variant="ghost" className="group absolute -translate-x-1/2 -translate-y-1/2 h-auto w-auto p-0 bg-transparent hover:bg-transparent" style={{
        top: glacier.top,
        left: glacier.left
      }} aria-label={`View details for ${glacier.name}`}>
            <span className="relative flex items-center justify-center">
              <span className="absolute inline-flex animate-ping rounded-lg bg-primary opacity-60" style={{
            width: "16px",
            height: "16px"
          }} />
              <span className={`relative inline-flex rounded-lg bg-primary transition-all duration-300 ${hovered === glacier.id || selected?.id === glacier.id ? "scale-150" : ""}`} style={{
            width: "8px",
            height: "8px"
          }} />
            </span>
            {hovered === glacier.id && <span className="font-mono pointer-events-none absolute left-1/2 top-full mt-3 -translate-x-1/2 whitespace-nowrap rounded-md border border-border bg-card/95 px-3 py-1.5 text-xs uppercase tracking-widest text-foreground backdrop-blur-md">
                {glacier.name}
              </span>}
          </Button>)}
      </div>

      {}
      <div className="relative z-10 hidden md:block">
        <div className="absolute left-8 top-8 w-full max-w-md lg:left-16 lg:top-16">
          <div className="rounded-md border border-border bg-card/85 p-8 backdrop-blur-xl shadow-2xl">
            
            <h2 className="font-default mt-5 text-4xl font-semibold leading-[1.02] tracking-tight lg:text-5xl">
              {"Where we "}
              <span className="italic text-primary">{"build"}</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              {"Serving Calgary and surrounding areas with premium exterior craftsmanship. Each project represents our commitment to architectural integrity and lasting quality."}
            </p>
            
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-8 left-8 z-10 hidden md:block lg:bottom-16 lg:left-16">
        <div className="rounded-md border border-border bg-card/85 p-6 backdrop-blur-xl shadow-2xl">
          
          <ul className="flex flex-col gap-1">
            {regions.map((region, i) => {
            const active = activeRegions.includes(region.id);
            return <li key={region.id} data-index={i}>
                  <Button onClick={() => toggleRegion(region.id)} variant="ghost" className="font-mono h-auto w-full justify-start gap-3 rounded-md px-3 py-2.5 text-left text-xs uppercase tracking-widest text-foreground transition-colors duration-200 hover:bg-accent">
                    <span className={`inline-flex h-2 w-2 rounded-lg transition-all duration-200 ${active ? "bg-primary" : "border border-border bg-transparent"}`} />
                    <span className={active ? "text-primary" : ""}>
                      {region.label}
                    </span>
                  </Button>
                </li>;
          })}
          </ul>
        </div>
      </div>

      {}
      

      {}
      <div className="relative z-10 flex min-h-screen flex-col md:hidden">
        {}
        <div className="relative h-[60vh] w-full">
          <div className="absolute left-5 top-5 right-5">
            <div className="rounded-md border border-border bg-card/85 p-5 backdrop-blur-xl">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                {"Service Coverage"}
              </p>
              <h2 className="font-default mt-3 text-3xl font-semibold leading-[1.02] tracking-tight">
                {"Where we "}
                <span className="italic text-primary">{"build"}</span>
              </h2>
            </div>
          </div>

          {}
          <div className="absolute inset-0">
            {visibleGlaciers.map((glacier, i) => <Button key={glacier.id} data-index={i} onClick={() => {
            handleMarkerClick(glacier);
            setSheetOpen(true);
          }} variant="ghost" className="absolute -translate-x-1/2 -translate-y-1/2 min-h-11 min-w-11 flex items-center justify-center p-0 bg-transparent hover:bg-transparent" style={{
            top: glacier.top,
            left: glacier.left
          }} aria-label={`View details for ${glacier.name}`}>
                <span className="relative flex items-center justify-center">
                  <span className="absolute inline-flex md:animate-ping rounded-lg bg-primary opacity-60" style={{
                width: "16px",
                height: "16px"
              }} />
                  <span className="relative inline-flex rounded-lg bg-primary" style={{
                width: "8px",
                height: "8px"
              }} />
                </span>
              </Button>)}
          </div>
        </div>

        {}
        <div className={`relative flex-1 border-t border-border bg-card/95 backdrop-blur-xl transition-all duration-300 ${sheetOpen ? "min-h-[60vh]" : ""}`}>
          <div className="px-5 py-5">
            <p className="font-mono mb-4 text-xs uppercase tracking-widest text-muted-foreground">locations&nbsp;</p>
            <div className="flex flex-wrap gap-2 max-w-full">
              {regions.map((region, i) => {
              const active = activeRegions.includes(region.id);
              return <Button key={region.id} data-index={i} onClick={() => toggleRegion(region.id)} variant={active ? "default" : "outline"} className={`font-mono h-auto min-h-9 py-1.5 px-3 gap-2 rounded-lg text-xs uppercase tracking-widest whitespace-normal text-left transition-colors duration-200 ${active ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90" : "border-border text-foreground hover:border-primary hover:text-primary"}`}>
                    <span className={`inline-flex h-1.5 w-1.5 rounded-lg ${active ? "bg-primary-foreground" : "bg-muted-foreground"}`} />
                    {region.label}
                  </Button>;
            })}
            </div>
          </div>

          {selected && sheetOpen && <div className="border-t border-border">
              <div className="relative aspect-video w-full overflow-hidden">
                <img src={selected.image} data-wvc-srcset={selected.srcset} data-wvc-sizes={selected.sizes} alt={selected.alt} className="h-full w-full object-cover" loading="lazy" decoding="async" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5 text-primary" />
                  <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                    {selected.coords}
                  </p>
                </div>
                <h3 className="font-default mt-3 text-2xl font-semibold leading-[1.15] tracking-tight">
                  {selected.name}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  {selected.summary}
                </p>
                <Link to="/ContactPage" className="mt-6 inline-block">
                  <Button className="h-12 gap-2 rounded-md bg-primary px-6 text-primary-foreground hover:bg-primary/90">
                    {"Request a Quote"}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>}
        </div>
      </div>
    </section>;
}